import { WorksheetCostCategory } from "generated/graphql";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { parseDecimalInput } from "utils/worksheet/worksheetNumbers";

const LINKED_SOURCES = ["duration", "travel", "duration_travel"];

const ROW_SOURCES = ["manual", "material", "duration", "travel", "duration_travel", "trucking"];

export const worksheetHelperErrors = (values: WorksheetItemInput): string[] => {
  const errors: string[] = [];
  const rows = values.costRows ?? [];
  const productionEnabled = !!values.helpers?.production?.enabled;

  const production = values.helpers?.production;

  if (production?.enabled) {
    const source = production.source ?? "item";
    if (!["item", "material"].includes(source)) {
      errors.push("Choose a production quantity source.");
    }
    if (source === "material") {
      const materialRow = rows.find((row) => row.rowUuid === production.materialRowUuid);
      if (!materialRow || materialRow.category !== WorksheetCostCategory.MATERIAL) {
        errors.push("Select a material row for the production rate.");
      }
    }
    if (!["hour", "day"].includes(production.period ?? "hour")) {
      errors.push("Choose an hourly or daily production rate.");
    }
    if (parseDecimalInput(production.rate ?? "") <= 0) {
      errors.push("Enter a positive production rate.");
    }
  }

  const travel = values.helpers?.travel;

  if (travel?.enabled) {
    if (parseDecimalInput(travel.roundTripHours ?? "") <= 0) {
      errors.push("Enter a positive round trip duration.");
    }
    if (travel.visits !== "") {
      const visits = Number(travel.visits);
      if (!Number.isFinite(visits) || visits < 0) {
        errors.push("Enter a valid number of travel visits.");
      }
    }
  }

  const allocations: Record<string, number> = {};

  rows.forEach((row) => {
    const source = row.quantitySource ?? "manual";

    if (!ROW_SOURCES.includes(source)) {
      errors.push("Choose a valid quantity calculation source.");
      return;
    }

    if (LINKED_SOURCES.includes(source)) {
      const hourly =
        [WorksheetCostCategory.LABOR, WorksheetCostCategory.EQUIPMENT].includes(row.category) &&
        String(row.unit ?? "").toLowerCase() === "hours";
      if (!hourly) {
        errors.push("Duration links require hourly labor or equipment.");
      }
      return;
    }

    if (source !== "trucking") {
      return;
    }

    if (row.category !== WorksheetCostCategory.TRUCKING) {
      errors.push("Only trucking rows may use the hauling planner.");
      return;
    }

    if (parseDecimalInput(row.truckCapacity ?? "") <= 0) {
      errors.push("Enter a positive truck capacity.");
      return;
    }

    if ((row.haulQuantityMode ?? "") === "manual") {
      if (parseDecimalInput(row.haulQuantity ?? "") <= 0) {
        errors.push("Enter the quantity to haul.");
      }
      const planning = planningError(row, productionEnabled);
      if (planning) {
        errors.push(planning);
      }
      return;
    }

    const materialRow = rows.find((candidate) => candidate.rowUuid === row.haulMaterialRowUuid);

    if (!materialRow || materialRow.category !== WorksheetCostCategory.MATERIAL) {
      errors.push("Select a material row to haul.");
      return;
    }

    const allocation = parseDecimalInput(row.haulAllocationPercent ?? "");

    if (allocation <= 0) {
      errors.push("Enter a positive hauling allocation.");
      return;
    }

    allocations[row.haulMaterialRowUuid] = (allocations[row.haulMaterialRowUuid] ?? 0) + allocation;

    if (allocations[row.haulMaterialRowUuid] > 100) {
      errors.push("Trucking allocations exceed 100% of the selected material.");
    }

    const planning = planningError(row, productionEnabled);

    if (planning) {
      errors.push(planning);
    }
  });

  return errors;
};

type WorksheetCostRowInputValues = WorksheetItemInput["costRows"][number];

function planningError(
  row: WorksheetCostRowInputValues,
  productionEnabled: boolean
): string | null {
  const planningHours = row.haulPlanningHours ?? "";

  if (planningHours !== "") {
    const hours = Number(planningHours);

    if (!Number.isFinite(hours) || hours <= 0) {
      return "Enter a positive trucking planning duration.";
    }
  }

  const billing = row.truckingUnit || "Hours";

  if (billing !== "Hours" && planningHours === "") {
    return null;
  }

  if (planningHours === "" && !productionEnabled && (row.haulQuantityMode || null) === null) {
    return "Enter a positive trucking planning duration.";
  }

  const cycle =
    parseDecimalInput(row.haulRoundTripHours ?? "") +
    parseDecimalInput(row.haulLoadHours ?? "") +
    parseDecimalInput(row.haulUnloadHours ?? "");

  if (cycle <= 0) {
    return "Enter a positive round trip duration.";
  }

  return null;
}
